import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  addDoc,
  deleteDoc,
  query,
  orderBy,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase';

const COLLECTION = 'automations';
const SETTINGS_COLLECTION = 'automationTemplatesSettings';
const SETTINGS_CONFIG_ID = 'config';

// ---- Group (tab) emojis — mirrors templatesImportsService's group emojis ----

export async function getAutomationGroupEmojis() {
  try {
    const snap = await getDoc(doc(db, SETTINGS_COLLECTION, SETTINGS_CONFIG_ID));
    return snap.exists() ? snap.data().groupEmojis || {} : {};
  } catch (e) {
    console.warn('Automation group emojis load:', e);
    return {};
  }
}

/** CRM: save one group's emoji (pass '' to reset to the default). */
export async function saveAutomationGroupEmoji(tabId, emoji) {
  await setDoc(
    doc(db, SETTINGS_COLLECTION, SETTINGS_CONFIG_ID),
    { groupEmojis: { [tabId]: emoji || '' }, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// ---- Version history (snapshots of the template document + its steps) ----

const versionsRef = (automationId) => collection(db, COLLECTION, automationId, 'versions');
const stepsRef = (automationId) => collection(db, COLLECTION, automationId, 'workflowSteps');

/** Read the current workflow steps as plain snapshot objects. */
async function snapshotSteps(automationId) {
  const snap = await getDocs(stepsRef(automationId));
  return snap.docs
    .map((d) => {
      const data = d.data();
      return {
        title: data.title || '',
        emoji: data.emoji || '',
        order: data.order || 0,
      };
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0));
}

function versionEntryFromDoc(data, steps, { version, notes, user }) {
  return {
    version: String(version).trim(),
    name: data.name || '',
    description: data.description || '',
    emojis: data.emojis || [],
    softwares: data.softwares || [],
    workflowSteps: steps,
    savedByUid: user?.uid || '',
    savedByName: user?.name || 'Unknown',
    savedAt: new Date().toISOString(),
    notes: notes || '',
  };
}

/** Lazily seed the first version entry from the current state (pre-history docs). */
async function seedInitialVersionIfNeeded(automationId, parentData) {
  const snap = await getDocs(versionsRef(automationId));
  if (!snap.empty) return;
  const steps = await snapshotSteps(automationId);
  await addDoc(
    versionsRef(automationId),
    versionEntryFromDoc(parentData, steps, {
      version: parentData.version || '1.0',
      notes: 'Initial version',
      user: { name: parentData.authorName || 'BBA Team' },
    })
  );
}

/** Snapshot the template's current state as a new version and bump the doc's version. */
export async function saveAutomationTemplateVersion({ automationId, version, notes = '', user = {} }) {
  if (!version || !String(version).trim()) throw new Error('A version number is required.');
  const mainRef = doc(db, COLLECTION, automationId);
  const snap = await getDoc(mainRef);
  if (!snap.exists()) throw new Error('Automation not found.');
  const data = snap.data();

  await seedInitialVersionIfNeeded(automationId, data);

  const steps = await snapshotSteps(automationId);
  await updateDoc(mainRef, { version: String(version).trim(), updatedAt: serverTimestamp() });
  await addDoc(versionsRef(automationId), versionEntryFromDoc(data, steps, { version, notes, user }));
}

export async function fetchAutomationVersionHistory(automationId) {
  if (!automationId) return [];
  const snap = await getDoc(doc(db, COLLECTION, automationId));
  if (snap.exists()) {
    await seedInitialVersionIfNeeded(automationId, snap.data());
  }
  const q = query(versionsRef(automationId), orderBy('savedAt', 'desc'));
  const versionsSnap = await getDocs(q);
  return versionsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Restore the template document and its steps from a prior version's snapshot
 * and append a new version entry recording the rollback (history is never destroyed).
 */
export async function rollbackAutomationToVersion(automationId, versionId, user = {}) {
  if (!automationId || !versionId) throw new Error('automationId and versionId are required');
  const versionSnap = await getDoc(doc(db, COLLECTION, automationId, 'versions', versionId));
  if (!versionSnap.exists()) throw new Error('Version not found.');
  const v = versionSnap.data();

  const mainRef = doc(db, COLLECTION, automationId);
  await updateDoc(mainRef, {
    name: v.name || '',
    description: v.description || '',
    emojis: v.emojis || [],
    softwares: v.softwares || [],
    version: v.version,
    updatedAt: serverTimestamp(),
  });

  // Replace the workflowSteps subcollection with the snapshot's steps
  const currentSteps = await getDocs(stepsRef(automationId));
  await Promise.all(currentSteps.docs.map((d) => deleteDoc(d.ref)));
  for (const step of v.workflowSteps || []) {
    await addDoc(stepsRef(automationId), {
      title: step.title || '',
      emoji: step.emoji || '',
      order: step.order || 0,
    });
  }

  await addDoc(
    versionsRef(automationId),
    versionEntryFromDoc(v, v.workflowSteps || [], {
      version: v.version,
      notes: `Rolled back to v${v.version}`,
      user,
    })
  );
}

/** Persist a category's display order (portal shows the same order). */
export async function saveAutomationsOrder(orderedIds) {
  const batch = writeBatch(db);
  orderedIds.forEach((id, idx) => {
    batch.update(doc(db, COLLECTION, id), { sortOrder: idx });
  });
  await batch.commit();
}

/**
 * Sort rows by manual order (sortOrder, written on reorder). Rows without one —
 * new templates and pre-reorder docs — come first, newest first, until placed.
 */
export function sortAutomationRows(rows) {
  return [...rows].sort((a, b) => {
    const ao = typeof a.sortOrder === 'number';
    const bo = typeof b.sortOrder === 'number';
    if (ao && bo) return a.sortOrder - b.sortOrder;
    if (ao !== bo) return ao ? 1 : -1;
    const ta = a.createdAt?.toMillis?.() ?? (a.createdAt?.seconds ?? 0) * 1000;
    const tb = b.createdAt?.toMillis?.() ?? (b.createdAt?.seconds ?? 0) * 1000;
    return tb - ta;
  });
}

/** Delete a template with its workflowSteps and versions subcollections. */
export async function deleteAutomationTemplate(automationId) {
  const [steps, versions] = await Promise.all([
    getDocs(stepsRef(automationId)),
    getDocs(versionsRef(automationId)),
  ]);
  await Promise.all([
    ...steps.docs.map((d) => deleteDoc(d.ref)),
    ...versions.docs.map((d) => deleteDoc(d.ref)),
  ]);
  await deleteDoc(doc(db, COLLECTION, automationId));
}
